import type { ReactNode } from 'react';
import { ShieldOff, Wifi } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useNetworkGate } from '@/hooks/useNetworkGate';

/**
 * Wraps the protected app shell. If the caller isn't on an allowlisted
 * office IP (per /api/me), renders a full-screen "office WiFi only"
 * card instead of the dashboard. Soft gate; for the hard gate, layer
 * Cloudflare Access on top.
 */
export function RequireNetwork({ children }: { children: ReactNode }) {
  const { data, isLoading } = useNetworkGate();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Skeleton className="h-24 w-72 rounded-lg" />
      </div>
    );
  }
  if (data && !data.allowed) {
    return <BlockedScreen ip={data.ip} country={data.country} />;
  }
  return <>{children}</>;
}

function BlockedScreen({ ip, country }: { ip: string; country: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-rose-500/5 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-elevated">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
            <ShieldOff className="h-5 w-5" />
          </span>
          <div>
            <div className="text-2xs uppercase tracking-wide text-muted-foreground">
              Blocked
            </div>
            <div className="text-base font-semibold leading-tight">
              Office network only
            </div>
          </div>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          The Flax Marketing Dashboard is restricted to the office WiFi. Please
          connect to the Flax network and reload — or use WireGuard if you're
          travelling.
        </p>

        <div className="mt-5 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-2xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Wifi className="h-3 w-3" />
            Your IP
          </div>
          <div className="mt-0.5 font-mono tabular-nums">
            {ip || 'unknown'} {country ? <span className="text-muted-foreground">· {country}</span> : null}
          </div>
        </div>

        <p className="mt-6 text-2xs text-muted-foreground">
          If you're on the office WiFi and seeing this, your public IP probably
          changed — ping the admin to update the allowlist.
        </p>
      </div>
    </div>
  );
}
